/* eslint-disable jsx-a11y/role-supports-aria-props */
import React, { useCallback, useEffect, useRef, useState } from "react";

import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Paper } from "@mui/material";
import InputBase from "@mui/material/InputBase";

import { SearchIcon } from "@/assets/icons";
import { FilterOptions } from "@/components/molecules";
import { SearchResult } from "@/shared/apis/searchApi";

import ResultsList from "./components/ResultsList";
import { StyledPopover, StyledQuickSearchButtonContainer } from "./styled";
import { getNextEntry, getPreviousEntry } from "./utils/array";
import { getSearchItemId } from "./utils/searchItemId";
import useSearch from "./utils/useSearch";

const QuickSearch = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [navigateTo, setNavigateTo] = useState<string>("");
    const [currentFocus, setCurrentFocus] = useState<SearchResult>(null);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const { t } = useTranslation();

    const isOpen = Boolean(anchorEl);

    const inputRef = useRef(null);
    const searchComponentRef = useRef(null);
    const searchPopperRef = useRef(null);

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleFocus = () => {
        if (searchComponentRef?.current) setAnchorEl(searchComponentRef.current);
    };

    const searchPopoverId = "search-popover-id";

    const { isBusy, searchContext, setSearchContext, message, results, setSearchWord, filters } = useSearch({
        isMobile: false,
    });

    const selectPrevious = () => {
        setCurrentFocus(getPreviousEntry(results, currentFocus));
    };

    const selectNext = () => {
        setCurrentFocus(getNextEntry(results, currentFocus));
    };

    const openEntry = (event, item?: SearchResult) => {
        handleClose();
        if (item) setCurrentFocus(item);
        if (currentFocus?.isAccessible) {
            if (location.pathname === currentFocus.url) {
                inputRef.current.value = "";
            } else {
                setNavigateTo(currentFocus.url);
            }
        }
    };

    useEffect(() => {
        if (!!navigateTo && !anchorEl) {
            navigate(navigateTo, { state: currentFocus.state });
            setNavigateTo("");
        }
    }, [navigateTo, anchorEl]);

    useEffect(() => {
        if (results.length > 0) {
            setCurrentFocus(results[0]);
        }
    }, [results]);

    const handleClick = useCallback((event) => {
        if (
            searchComponentRef &&
            searchPopperRef &&
            searchComponentRef.current &&
            !searchComponentRef.current.contains(event.target) &&
            searchPopperRef.current &&
            !searchPopperRef.current.contains(event.target)
        ) {
            handleClose();
        }
    }, []);

    useEffect(() => {
        document.addEventListener("click", handleClick);
        return () => document.removeEventListener("click", handleClick);
    }, []);

    const searchInputProps = {
        autoCapitalize: "none",
        spellCheck: false,
        autoComplete: "no",
        "aria-busy": isBusy,
        "aria-labelledby": "quick_search-label",
        "data-testid": "quick_search-input",
        ...(currentFocus != null && { "aria-activedescendant": getSearchItemId(currentFocus) }),
    };

    return (
        <>
            <StyledQuickSearchButtonContainer
                ref={searchComponentRef}
                aria-owns={searchPopoverId}
                aria-haspopup="listbox"
                role="search"
                aria-expanded={isOpen}
                aria-controls={searchPopoverId}
            >
                <SearchIcon />
                <label className="sr-only" id="quick_search-label">
                    {t("Common.Search")}
                </label>
                <InputBase
                    type="search"
                    inputRef={inputRef}
                    className={clsx(isOpen && "popup-visible")}
                    classes={{ root: "input-container", input: "input" }}
                    onChange={(e) => setSearchWord(e.target.value)}
                    onFocus={(_event) => {
                        handleFocus();
                    }}
                    onKeyDown={(event) => {
                        if (event.key === "ArrowUp" && isOpen && results.length > 0) {
                            event.preventDefault();
                            selectPrevious();
                        } else if (event.key === "ArrowDown" && isOpen && results.length > 0) {
                            event.preventDefault();
                            selectNext();
                        } else if (event.key === "Enter" && isOpen) {
                            if (results.length > 0) {
                                openEntry(event);
                            }
                            event.preventDefault();
                        } else if (event.key === "Escape" && isOpen) {
                            handleClose();
                        } else if (!isOpen) {
                            handleFocus();
                        }
                    }}
                    data-testid="quick_search-input-search"
                    placeholder={t("Common.Search")}
                    inputProps={searchInputProps}
                />
            </StyledQuickSearchButtonContainer>

            <StyledPopover
                ref={searchPopperRef}
                id={searchPopoverId}
                open={!!anchorEl}
                anchorEl={anchorEl}
                style={{ zIndex: 1300 }}
                placement="bottom-end"
            >
                <Paper className="popover-paper">
                    {filters.length > 2 && (
                        <FilterOptions
                            selectedFilterId={searchContext}
                            availableFilters={filters}
                            onChangeFilter={setSearchContext}
                        />
                    )}
                    <Alert severity="info" sx={{ margin: "8px 16px" }}>
                        {t("Search.InfoAlert")}
                    </Alert>
                    {message !== null && <p role="status">{message}</p>}
                    {message === null && (
                        <ResultsList
                            labelledby="quick_search-label"
                            focus={currentFocus}
                            openEntry={openEntry}
                            setFocus={setCurrentFocus}
                            results={results}
                        />
                    )}
                </Paper>
            </StyledPopover>
        </>
    );
};

export default QuickSearch;
